import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BedDouble, Users, Calculator, ArrowRight, Sparkles } from 'lucide-react';

const BADGES = {
  available: { label: 'Available for your dates', cls: 'bg-[#2E6B4A] text-white' },
  unavailable: { label: 'Unavailable for your dates', cls: 'bg-[#9E4040] text-white' },
  confirm: { label: 'Contact WVC to confirm', cls: 'bg-[#A87C4F] text-white' },
};

export const VenueCard = ({ venue, settings = {} }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dateQuery = (() => {
    const sp = new URLSearchParams(location.search);
    const ci = sp.get('check_in');
    const co = sp.get('check_out');
    return ci && co ? `?check_in=${ci}&check_out=${co}` : '';
  })();
  const badge = venue.availability_status ? BADGES[venue.availability_status] : null;
  const showAvailability = settings.show_availability !== false;
  return (
    <div
      className="group bg-white rounded-2xl overflow-hidden border border-[#1A3626]/10 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(26,54,38,0.10)] transition-all duration-300 font-wvc-body"
      data-testid={`venue-card-${venue.slug}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#F3F1E7]">
        {venue.image && <img src={venue.image} alt={venue.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
        {badge && showAvailability && (
          <span className={`absolute top-3 right-3 px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold ${badge.cls}`} data-testid={`venue-badge-${venue.slug}`}>
            {badge.label}
          </span>
        )}
        {venue.has_calculator && (
          <span className="absolute bottom-3 left-3 px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold bg-white/90 text-[#1A3626] flex items-center gap-1">
            <Calculator className="w-3 h-3" strokeWidth={1.5} /> Wedding calculator
          </span>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-wvc-display text-xl font-semibold text-[#1A201C] leading-tight">{venue.name}</h3>
            <p className="text-xs text-[#748278] mt-1">{[venue.city, venue.venue_type].filter(Boolean).join(' · ')}</p>
          </div>
        </div>
        {settings.show_capacity !== false && (venue.rooms || venue.event_capacity) && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-[#3E4C43]">
            {venue.rooms && <span className="flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" strokeWidth={1.5} /> {venue.rooms} rooms</span>}
            {venue.stay_capacity && <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" strokeWidth={1.5} /> Stay {venue.stay_capacity}</span>}
            {venue.event_capacity && <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" strokeWidth={1.5} /> Events {venue.event_capacity}</span>}
          </div>
        )}
        {venue.feature && (
          <p className="text-xs text-[#3E4C43] mt-3 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[#C6A87C]" strokeWidth={1.5} /> {venue.feature}</p>
        )}
        {settings.show_pricing !== false && venue.starting_price_label && (
          <p className="text-sm text-[#1A3626] font-semibold mt-3">{venue.starting_price_label}</p>
        )}
        <div className="flex gap-2 mt-5">
          <button
            onClick={() => navigate(`/venues/${venue.slug}${dateQuery}`)}
            className="flex-1 bg-[#1A3626] text-white hover:bg-[#254834] rounded-full py-2.5 text-xs uppercase tracking-wider transition-all duration-300 inline-flex items-center justify-center gap-1"
            data-testid={`venue-view-${venue.slug}`}
          >
            View Venue <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
          {venue.has_calculator && (
            <button
              onClick={() => navigate(`/venues/${venue.slug}${dateQuery}#calculator`)}
              className="flex-1 border border-[#1A3626] text-[#1A3626] hover:bg-[#F3F1E7] rounded-full py-2.5 text-xs uppercase tracking-wider transition-all duration-300"
              data-testid={`venue-calc-${venue.slug}`}
            >
              Calculate Cost
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const VenueCardGrid = ({ venues, settings }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="venue-card-grid">
    {venues.map(v => <VenueCard key={v.slug} venue={v} settings={settings} />)}
  </div>
);
